import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiTrendingDown, FiLayers, FiZap, FiCpu, FiBox, FiDollarSign, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { getProjects } from '../../api/projects';
import { getArchitecturesByProject } from '../../api/architectures';

const SKU_MONTHLY_USD: Record<string, number> = {
  F2: 263, F4: 526, F8: 1051, F16: 2102, F32: 4205, F64: 8410,
  F128: 16819, F256: 33638, F512: 67277, F1024: 134554,
};
const SKU_CUS: Record<string, number> = {
  F2: 2, F4: 4, F8: 8, F16: 16, F32: 32, F64: 64,
  F128: 128, F256: 256, F512: 512, F1024: 1024,
};
const SKU_OPTIONS = ['F2', 'F4', 'F8', 'F16', 'F32', 'F64', 'F128', 'F256', 'F512', 'F1024'];

// Palette de couleurs pour les workspaces
const WS_COLORS = ['#0EA5E9', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#6366F1', '#14B8A6'];

interface WorkspaceCost {
  id: string;
  name: string;
  shareUSD: number;
  sharePercent: number;
}

interface CapacityCost {
  id: string;
  name: string;
  sku: string;
  monthlyUSD: number;
  cus: number;
  workspaces: WorkspaceCost[];
}

interface ArchitectureFinOps {
  archId: string;
  archName: string;
  projectName: string;
  totalUSD: number;
  capacities: CapacityCost[];
  recommendations: string[];
}

function formatUSD(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function skuColor(sku: string): { bg: string; text: string; border: string } {
  const idx = SKU_OPTIONS.indexOf(sku);
  if (idx <= 1) return { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' };
  if (idx <= 3) return { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' };
  if (idx <= 5) return { bg: '#F5F3FF', text: '#7C3AED', border: '#DDD6FE' };
  return { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' };
}

function buildRecommendations(cap: CapacityCost): string[] {
  const recs: string[] = [];
  const wsCount = cap.workspaces.length;
  const skuIdx = SKU_OPTIONS.indexOf(cap.sku);
  if (cap.sku === 'F2' && wsCount > 2)
    recs.push(`"${cap.name}" (F2) héberge ${wsCount} workspaces — envisagez F4 pour de meilleures performances.`);
  if (wsCount === 0)
    recs.push(`"${cap.name}" n'a aucun workspace — capacité inutilisée (${formatUSD(cap.monthlyUSD)}/mois).`);
  if (wsCount === 1 && skuIdx > 1)
    recs.push(`"${cap.name}" (${cap.sku}) n'a qu'un seul workspace — ${SKU_OPTIONS[skuIdx - 1]} pourrait suffire.`);
  return recs;
}

const FabricFinOpsTab: React.FC = () => {
  const [architectures, setArchitectures] = useState<ArchitectureFinOps[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedArchs, setExpandedArchs] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const projects = await getProjects() as any[];
        const result: ArchitectureFinOps[] = [];
        for (const proj of projects) {
          try {
            const archs = await getArchitecturesByProject(proj.id);
            for (const arch of archs) {
              const nodes: any[] = (arch.diagram_data as any)?.nodes || [];
              const capacityNodes = nodes.filter(n => n.data?.type === 'fabric_capacity');
              if (capacityNodes.length === 0) continue;

              const capacities: CapacityCost[] = capacityNodes.map(cap => {
                const sku: string = cap.data?.params?.sku || 'F2';
                const monthlyUSD = SKU_MONTHLY_USD[sku] ?? 0;
                const cus = SKU_CUS[sku] ?? 0;
                const domainsInCap = new Set(nodes.filter(n => n.data?.type === 'fabric_domain' && n.parentNode === cap.id).map(n => n.id));
                const wsNodes = nodes.filter(n =>
                  n.data?.type === 'fabric_workspace' &&
                  (n.parentNode === cap.id || domainsInCap.has(n.parentNode || ''))
                );
                const wsCount = wsNodes.length || 1;
                const workspaces: WorkspaceCost[] = wsNodes.map(ws => ({
                  id: ws.id,
                  name: ws.data?.label || ws.data?.params?.display_name || 'Workspace',
                  shareUSD: monthlyUSD / wsCount,
                  sharePercent: 100 / wsCount,
                }));
                return { id: cap.id, name: cap.data?.label || cap.data?.params?.display_name || cap.data?.params?.name || 'Capacity', sku, monthlyUSD, cus, workspaces };
              });

              const totalUSD = capacities.reduce((s, c) => s + c.monthlyUSD, 0);
              const recommendations = capacities.flatMap(buildRecommendations);
              result.push({ archId: arch.id, archName: arch.name, projectName: proj.name, totalUSD, capacities, recommendations });
            }
          } catch { /* non bloquant */ }
        }
        setArchitectures(result);
        // Expand all by default
        setExpandedArchs(new Set(result.map(a => a.archId)));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const grandTotal = architectures.reduce((s, a) => s + a.totalUSD, 0);
  const totalCapacities = architectures.reduce((s, a) => s + a.capacities.length, 0);
  const totalCUs = architectures.reduce((s, a) => s + a.capacities.reduce((sc, c) => sc + c.cus, 0), 0);
  const totalWorkspaces = architectures.reduce((s, a) => s + a.capacities.reduce((sc, c) => sc + c.workspaces.length, 0), 0);
  const allRecs = architectures.flatMap(a => a.recommendations);

  const toggleArch = (id: string) => {
    setExpandedArchs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: 'linear-gradient(135deg, #0EA5E9, #8B5CF6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}>
          <FiZap size={22} color="white" />
        </div>
        <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>Calcul des coûts Microsoft Fabric...</span>
      </div>
    );
  }

  if (architectures.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '5rem 2rem', textAlign: 'center' }}>
        <div style={{
          width: 72, height: 72, borderRadius: '1.25rem',
          background: 'linear-gradient(135deg, #f0f9ff, #faf5ff)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem',
          border: '1px solid #e5e7eb',
        }}>
          <FiLayers size={32} color="#D1D5DB" />
        </div>
        <p style={{ fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Aucune architecture Fabric trouvée</p>
        <p style={{ color: '#9CA3AF', fontSize: '0.85rem', maxWidth: '380px' }}>
          Créez une architecture avec des nœuds <strong>fabric_capacity</strong> pour voir le chargeback FinOps.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* ── KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>

        {/* Coût total */}
        <div style={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0EA5E9 100%)',
          borderRadius: '1rem', padding: '1.5rem', color: 'white',
          boxShadow: '0 4px 16px rgba(14,165,233,0.25)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Coût mensuel estimé</span>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}>
              <FiDollarSign size={18} color="white" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.25rem' }}>{formatUSD(grandTotal)}</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>Toutes architectures Fabric</div>
        </div>

        {/* Architectures */}
        <div style={{
          background: 'linear-gradient(135deg, #6d28d9 0%, #8B5CF6 100%)',
          borderRadius: '1rem', padding: '1.5rem', color: 'white',
          boxShadow: '0 4px 16px rgba(139,92,246,0.25)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Architectures</span>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}>
              <FiLayers size={18} color="white" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.25rem' }}>{architectures.length}</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>avec capacités Fabric</div>
        </div>

        {/* Capacités / CUs */}
        <div style={{
          background: 'linear-gradient(135deg, #1d4ed8 0%, #3B82F6 100%)',
          borderRadius: '1rem', padding: '1.5rem', color: 'white',
          boxShadow: '0 4px 16px rgba(59,130,246,0.25)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Capacités</span>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}>
              <FiCpu size={18} color="white" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.25rem' }}>{totalCapacities}</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>{totalCUs} CUs provisionnés</div>
        </div>

        {/* Workspaces */}
        <div style={{
          background: 'linear-gradient(135deg, #b45309 0%, #F59E0B 100%)',
          borderRadius: '1rem', padding: '1.5rem', color: 'white',
          boxShadow: '0 4px 16px rgba(245,158,11,0.25)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Workspaces</span>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}>
              <FiBox size={18} color="white" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.25rem' }}>{totalWorkspaces}</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>tous projets confondus</div>
        </div>
      </div>

      {/* ── Recommandations ── */}
      {allRecs.length > 0 && (
        <div style={{
          background: '#FFFBEB',
          border: '1px solid #FDE68A',
          borderLeft: '4px solid #F59E0B',
          borderRadius: '0.875rem',
          padding: '1.25rem 1.5rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <FiAlertTriangle size={16} color="#D97706" />
            <span style={{ fontWeight: 700, color: '#92400E', fontSize: '0.9rem' }}>
              {allRecs.length} recommandation{allRecs.length > 1 ? 's' : ''} FinOps
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {allRecs.map((rec, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', background: 'white', borderRadius: '0.6rem', padding: '0.75rem 1rem', border: '1px solid #FDE68A' }}>
                <FiTrendingDown size={14} color="#D97706" style={{ marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.82rem', color: '#78350F' }}>{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Détail par architecture ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {architectures.map(arch => {
          const isExpanded = expandedArchs.has(arch.archId);
          return (
            <div key={arch.archId} style={{
              background: 'white',
              borderRadius: '1rem',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
              overflow: 'hidden',
            }}>
              {/* Header architecture */}
              <div
                onClick={() => toggleArch(arch.archId)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '1.1rem 1.5rem',
                  background: 'linear-gradient(135deg, #f8faff 0%, #f5f3ff 100%)',
                  borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: '0.6rem',
                    background: 'linear-gradient(135deg, #0EA5E9, #8B5CF6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FiZap size={18} color="white" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem' }}>{arch.archName}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '1px' }}>
                      {arch.projectName} · {arch.capacities.length} capacité{arch.capacities.length > 1 ? 's' : ''} · {arch.capacities.reduce((s, c) => s + c.workspaces.length, 0)} workspace{arch.capacities.reduce((s, c) => s + c.workspaces.length, 0) > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #0f766e, #0EA5E9)',
                    color: 'white',
                    borderRadius: '0.5rem',
                    padding: '0.3rem 0.875rem',
                    fontSize: '0.88rem',
                    fontWeight: 700,
                  }}>
                    {formatUSD(arch.totalUSD)}<span style={{ fontSize: '0.7rem', fontWeight: 400, opacity: 0.8 }}>/mois</span>
                  </div>
                  {isExpanded ? <FiChevronUp size={16} color="#6B7280" /> : <FiChevronDown size={16} color="#6B7280" />}
                </div>
              </div>

              {/* Capacités — expandable */}
              {isExpanded && (
                <div style={{ padding: '1rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {arch.capacities.map(cap => {
                    const colors = skuColor(cap.sku);
                    return (
                      <div key={cap.id} style={{
                        border: '1px solid #f3f4f6',
                        borderRadius: '0.75rem',
                        overflow: 'hidden',
                      }}>
                        {/* Header capacité */}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '0.875rem 1.25rem',
                          background: '#fafafa',
                          borderBottom: '1px solid #f3f4f6',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <img
                              src="/icons/fabric/fabric_capacity.svg"
                              alt=""
                              style={{ width: 22, height: 22 }}
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                            <span style={{ fontWeight: 600, color: '#1F2937', fontSize: '0.88rem' }}>{cap.name}</span>
                            <span style={{
                              fontSize: '0.72rem', fontWeight: 700,
                              background: colors.bg, color: colors.text,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '9999px', padding: '2px 8px',
                            }}>
                              {cap.sku}
                            </span>
                            <span style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{cap.cus} CUs</span>
                          </div>
                          <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.9rem' }}>
                            {formatUSD(cap.monthlyUSD)}<span style={{ fontWeight: 400, fontSize: '0.72rem', color: '#9CA3AF' }}>/mois</span>
                          </span>
                        </div>

                        {/* Workspace breakdown */}
                        {cap.workspaces.length > 0 ? (
                          <div style={{ padding: '1rem 1.25rem' }}>
                            {/* Barre segmentée */}
                            <div style={{ display: 'flex', height: '8px', borderRadius: '9999px', overflow: 'hidden', marginBottom: '0.875rem', gap: '2px' }}>
                              {cap.workspaces.map((ws, wi) => (
                                <div
                                  key={ws.id}
                                  style={{
                                    flex: ws.sharePercent,
                                    background: WS_COLORS[wi % WS_COLORS.length],
                                    borderRadius: wi === 0 ? '9999px 0 0 9999px' : wi === cap.workspaces.length - 1 ? '0 9999px 9999px 0' : '0',
                                    minWidth: '4px',
                                  }}
                                  title={`${ws.name}: ${Math.round(ws.sharePercent)}%`}
                                />
                              ))}
                            </div>

                            {/* Liste workspaces */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                              {cap.workspaces.map((ws, wi) => (
                                <div key={ws.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                  <div style={{
                                    width: 10, height: 10, borderRadius: '2px', flexShrink: 0,
                                    background: WS_COLORS[wi % WS_COLORS.length],
                                  }} />
                                  <span style={{ flex: 1, fontSize: '0.8rem', color: '#374151', fontWeight: 500 }}>{ws.name}</span>
                                  <span style={{ fontSize: '0.78rem', color: '#6B7280', minWidth: '40px', textAlign: 'right' }}>
                                    {Math.round(ws.sharePercent)}%
                                  </span>
                                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827', minWidth: '70px', textAlign: 'right' }}>
                                    {formatUSD(ws.shareUSD)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: '0.75rem 1.25rem' }}>
                            <span style={{ fontSize: '0.78rem', color: '#9CA3AF', fontStyle: 'italic' }}>
                              Aucun workspace dans cette capacité
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Note de bas de page */}
      <div style={{
        marginTop: '1.5rem',
        padding: '0.875rem 1.25rem',
        background: '#F9FAFB',
        borderRadius: '0.75rem',
        border: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <FiZap size={13} color="#9CA3AF" />
        <p style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: 0 }}>
          Estimation basée sur le tarif West Europe ($0.18/CU-heure, 730h/mois). Coût distribué équitablement entre les workspaces de chaque capacité.
        </p>
      </div>
    </div>
  );
};

export default FabricFinOpsTab;
